import { PlusIcon, XIcon } from '@primer/octicons-react';
import { addHours } from 'date-fns';
import { FieldArray } from 'formik';
import React, { Suspense } from 'react';
import { graphql, useFragment } from 'react-relay';

import { PromotionProductEventUpsertDialog_promotion$key } from '../../../relay/__generated__/PromotionProductEventUpsertDialog_promotion.graphql';
import { PromotionProductEventUpsertDialog_promotionProductEventUpsertMutation } from '../../../relay/__generated__/PromotionProductEventUpsertDialog_promotionProductEventUpsertMutation.graphql';
import { formatDate } from '../../../utils/date';
import { isNullable } from '../../../utils/is';
import Button from '../../core/Button';
import Dialog, { DialogProps } from '../../core/Dialog';
import FormLayout from '../../core/FormLayout';
import Grid from '../../core/Grid';
import IconButton from '../../core/IconButton';
import ItemList from '../../core/ItemList';
import MutationFormik, { MutationFormikProps } from '../../core/MutationFormik';
import NonFieldError from '../../core/NonFieldError';
import NumberField from '../../core/NumberField';
import Text from '../../core/Text';
import TextField from '../../core/TextField';
import View from '../../core/View';
import ProductSelectField from '../../product/ProductSelectField';

const PromotionProductEventUpsertDialog_promotion = graphql`
  fragment PromotionProductEventUpsertDialog_promotion on Promotion {
    id

    nextOccurrences(limit: 1)
    startAt

    productEvent {
      title
      durationHours
      durationDays
      offsetHours

      discounts {
        product {
          id
        }
        amount
      }
    }
  }
`;

type Props = {
  promotion: PromotionProductEventUpsertDialog_promotion$key;
} & DialogProps &
  Pick<
    MutationFormikProps<PromotionProductEventUpsertDialog_promotionProductEventUpsertMutation>,
    'connections' | 'config' | 'onSubmit'
  >;

const PromotionProductEventUpsertDialog = ({ promotion, connections, config, onSubmit, ...props }: Props) => {
  const defaultDiscountValue = { productId: '', amount: 0 };

  const { id, productEvent, nextOccurrences, startAt } = useFragment(
    PromotionProductEventUpsertDialog_promotion,
    promotion,
  );

  const startDate = new Date(nextOccurrences?.[0] || startAt);

  const dialogActionText = productEvent ? '수정하기' : '입력하기';

  return (
    <Dialog {...props}>
      <Dialog.Header>
        <Text sx={{ fontWeight: 'bold', fontSize: 3 }}>할인 이벤트 {dialogActionText}</Text>
      </Dialog.Header>
      <MutationFormik<PromotionProductEventUpsertDialog_promotionProductEventUpsertMutation>
        mutation={graphql`
          mutation PromotionProductEventUpsertDialog_promotionProductEventUpsertMutation(
            $input: PromotionProductEventUpsertInput!
          ) {
            promotionProductEventUpsert(input: $input) {
              title

              offsetHours

              durationHours
              durationDays

              discounts {
                product {
                  id
                }
                amount
              }
            }
          }
        `}
        initialValues={{
          promotionId: id,
          title: productEvent?.title,
          offsetHours: productEvent?.offsetHours ?? 0,
          durationHours: productEvent?.durationHours ?? 0,
          durationDays: productEvent?.durationDays ?? 0,
          discounts:
            (productEvent?.discounts ?? []).length > 0
              ? (productEvent?.discounts ?? []).map(({ product, amount }) => ({ productId: product.id, amount }))
              : [defaultDiscountValue],
        }}
        connections={connections}
        config={config}
        onSubmit={onSubmit}
      >
        {({ values, setFieldValue, handleSubmit, errors }, { nonFieldErrorRef, scrollContainerRef }) => {
          const isDurationError = errors.durationDays ?? errors.durationHours;
          const duration = Number(values?.durationDays ?? 0) * 24 + Number(values?.durationHours ?? 0);
          return (
            <>
              <Dialog.Body ref={scrollContainerRef}>
                <NonFieldError ref={nonFieldErrorRef} sx={{ marginBottom: 3 }} />
                <FormLayout>
                  <Text sx={{ fontSize: 3, fontWeight: 'bold' }}>할인 이벤트 정보</Text>
                  <TextField
                    label={'할인 이벤트명'}
                    required
                    placeholder={'할인 이벤트명'}
                    name={'title'}
                    caption={'* 스토어 탭에서 할인 이벤트명이 노출됩니다'}
                  />
                  {/* <CheckboxField */}
                  {/*   label={'비회원 노출 여부'} */}
                  {/*   name={'exposeToAnonymous'} */}
                  {/*   caption={'체크 시 로그인하지 않은 사용자에게도 이 할인 이벤트가 노출됩니다.'} */}
                  {/* /> */}
                  <Grid gapX={2}>
                    <Grid.Unit size={1 / 2}>
                      <NumberField
                        min={0}
                        label={'오프셋 (시간)'}
                        name={'offsetHours'}
                        caption={`${formatDate(
                          addHours(startDate, Number(values?.offsetHours ?? 0)).toISOString(),
                          'P a hh:mm',
                        )}에 시작돼요`}
                        placeholder={'오프셋 입력'}
                        required
                        validate={(v) => {
                          if (isNullable(v)) return '0 이상의 숫자를 입력해주세요';
                        }}
                      />
                    </Grid.Unit>
                    <Grid.Unit size={1 / 4}>
                      <NumberField
                        min={0}
                        value={values.durationDays ? `${values.durationDays}` : ''}
                        label={'지속시간 *'}
                        name={'durationDays'}
                        placeholder={'일 입력'}
                        caption={`${formatDate(
                          addHours(startDate, Number(values?.offsetHours ?? 0) + duration).toISOString(),
                          'P a hh:mm',
                        )}에 종료돼요`}
                        validate={() => {
                          if (duration < 1) return '1시간 이상으로 입력해주세요';
                        }}
                      />
                    </Grid.Unit>
                    <Grid.Unit size={1 / 4}>
                      <NumberField
                        min={0}
                        value={values.durationHours ? `${values.durationHours}` : ''}
                        label={'지속시간 - 시간'}
                        labelConfig={{ sx: { visibility: 'hidden' } }}
                        name={'durationHours'}
                        placeholder={'시간 입력'}
                        validationStatus={!!isDurationError ? 'error' : undefined}
                        onChange={(e) => {
                          if (e.target.value === '') {
                            setFieldValue('durationHours', undefined);
                            return;
                          }

                          const value = Number(e.target.value);
                          if (value / 24 >= 1) {
                            const day = Math.floor(value / 24) + Number(values.durationDays ?? 0);
                            const hour = value % 24;
                            setFieldValue('durationDays', day);
                            setFieldValue('durationHours', hour);
                          } else {
                            setFieldValue('durationHours', value);
                          }
                        }}
                      />
                    </Grid.Unit>
                  </Grid>
                  <FieldArray name={'discounts'}>
                    {({ remove, insert }) => (
                      <View>
                        <ItemList
                          items={values?.discounts || []}
                          renderItem={(_, i) => (
                            <Suspense>
                              <Grid sx={{ alignItems: 'flex-end' }} gapX={2}>
                                <Grid.Unit size={'max'}>
                                  <Grid gapX={2}>
                                    <Grid.Unit size={1 / 2}>
                                      <ProductSelectField
                                        label={'판매 상품'}
                                        name={`discounts.${i}.productId`}
                                        placeholder={'판매 상품 선택'}
                                        variables={{
                                          filters: {
                                            status_In: ['active'],
                                          },
                                        }}
                                        required
                                      />
                                    </Grid.Unit>
                                    <Grid.Unit size={1 / 2}>
                                      <NumberField
                                        label={'할인 금액'}
                                        name={`discounts.${i}.amount`}
                                        placeholder={'할인 금액'}
                                        required
                                      />
                                    </Grid.Unit>
                                  </Grid>
                                </Grid.Unit>
                                <Grid.Unit size={'min'}>
                                  <IconButton
                                    icon={XIcon}
                                    onClick={() => remove(i)}
                                    disabled={(values?.discounts || []).length < 2}
                                    aria-label={`remove #${i} productDiscount`}
                                  />
                                </Grid.Unit>
                              </Grid>
                            </Suspense>
                          )}
                          renderItemWrapper={(children, _, i) => (
                            <View key={i} sx={{ marginTop: i > 0 ? 3 : 0 }}>
                              {children}
                            </View>
                          )}
                        />
                        <View sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 3 }}>
                          <Button
                            variant={'outline'}
                            onClick={() => insert((values?.discounts || []).length, defaultDiscountValue)}
                            leadingIcon={PlusIcon}
                          >
                            추가하기
                          </Button>
                        </View>
                      </View>
                    )}
                  </FieldArray>
                </FormLayout>
              </Dialog.Body>
              <Dialog.Footer>
                <Button
                  size={'large'}
                  variant={'primary'}
                  onClick={() => {
                    handleSubmit();
                  }}
                >
                  {dialogActionText}
                </Button>
              </Dialog.Footer>
            </>
          );
        }}
      </MutationFormik>
    </Dialog>
  );
};

export default PromotionProductEventUpsertDialog;
